import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { useReactionSounds } from '@/hooks/useReactionSounds';
import { cn } from '@/lib/utils';

interface FloatingReaction {
  id: string;
  emoji: string;
  x: number;
  userId: string;
  userName?: string;
}

interface ArenaFloatingReactionsProps {
  arenaId: string;
  className?: string;
}

const REACTION_EMOJIS = ['❤️', '🔥', '😂', '👏', '🎉', '💕', '😍', '🙌'];

export const ArenaFloatingReactions: React.FC<ArenaFloatingReactionsProps> = ({
  arenaId,
  className,
}) => {
  const { user } = useAuthContext();
  const [reactions, setReactions] = useState<FloatingReaction[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const { playReactionSound, playApplause } = useReactionSounds();

  // Subscribe to reactions via Supabase Realtime
  useEffect(() => {
    const channel = supabase.channel(`arena-reactions-${arenaId}`);

    channel
      .on('broadcast', { event: 'reaction' }, (payload) => {
        const reaction: FloatingReaction = {
          id: `${Date.now()}-${Math.random()}`,
          emoji: payload.payload.emoji,
          x: payload.payload.x || Math.random() * 80 + 10,
          userId: payload.payload.userId,
          userName: payload.payload.userName,
        };

        setReactions((prev) => [...prev, reaction]);

        // Play sound effect for the reaction
        const emoji = payload.payload.emoji;
        if (emoji === '👏' || emoji === '🙌') {
          playApplause();
        } else {
          playReactionSound(emoji);
        }

        // Remove reaction after animation completes
        setTimeout(() => {
          setReactions((prev) => prev.filter((r) => r.id !== reaction.id));
        }, 3000);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [arenaId, playReactionSound, playApplause]);

  // Send a reaction
  const sendReaction = useCallback(
    async (emoji: string) => {
      if (!user) return;

      const channel = supabase.channel(`arena-reactions-${arenaId}`);
      
      await channel.send({
        type: 'broadcast',
        event: 'reaction',
        payload: {
          emoji,
          userId: user.id,
          userName: user.user_metadata?.name || 'Someone',
          x: Math.random() * 80 + 10,
        },
      });

      setShowPicker(false);
    },
    [arenaId, user]
  );

  return (
    <>
      {/* Floating reactions container */}
      <div
        className={cn(
          'absolute inset-0 pointer-events-none overflow-hidden z-20',
          className
        )}
      >
        {reactions.map((reaction) => (
          <div
            key={reaction.id}
            className="absolute animate-float-up"
            style={{
              left: `${reaction.x}%`,
              bottom: '10%',
            }}
          >
            <span className="text-4xl drop-shadow-lg">{reaction.emoji}</span>
          </div>
        ))}
      </div>

      {/* Reaction button and picker */}
      <div className="absolute bottom-24 right-4 z-30">
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center text-2xl hover:scale-110 transition-transform"
        >
          ❤️
        </button>

        {/* Quick reaction picker */}
        {showPicker && (
          <div className="absolute bottom-14 right-0 bg-white rounded-2xl shadow-xl p-2 flex gap-1 animate-scale-in">
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => sendReaction(emoji)}
                className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center text-xl hover:scale-125 transition-transform"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* CSS for float animation */}
      <style>{`
        @keyframes float-up {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          50% {
            opacity: 1;
            transform: translateY(-150px) scale(1.2);
          }
          100% {
            opacity: 0;
            transform: translateY(-300px) scale(0.8);
          }
        }
        .animate-float-up {
          animation: float-up 3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default ArenaFloatingReactions;
